import { type FC, type ChangeEvent, useState, useContext, useTransition, useCallback } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import classnames from 'classnames';

import { useIntl, defineMessages, FormattedMessage } from 'dibs-react-intl';
import { Input } from 'dibs-elements/exports/Input.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import QuestionMark from 'dibs-icons/exports/QuestionMark.ts';
import Close from 'dibs-icons/exports/legacy/Close.ts';
import MagnifyingGlass from 'dibs-icons/exports/legacy/MagnifyingGlass.ts';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';
import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { ACTIONS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskContactSupportSearch_viewer$key } from './__generated__/GetHelpZendeskContactSupportSearch_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskArticlesSearchSharedStyles.scss';

const MIN_SEARCH_LENGTH = 3;

const messages = defineMessages({
    placeholder: {
        id: 'getHelp.GetHelpZendeskArticlesSearch.placeholder',
        defaultMessage: 'Search 1stDibs Help',
    },
});

const GetHelpZendeskContactSupportSearch: FC<{
    viewer: GetHelpZendeskContactSupportSearch_viewer$key;
}> = ({ viewer: viewerRef }) => {
    const intl = useIntl();

    const {
        dispatch,
        state: { contactSupportSearchQuery, isSeller },
    } = useContext(GetHelpZendeskContext);
    const [query, setQuery] = useState(contactSupportSearchQuery);
    const [hasSearched, setHasSearched] = useState(false);
    const [fetching, startTransition] = useTransition();

    const [viewer, refetch] = useRefetchableFragment(
        graphql`
            fragment GetHelpZendeskContactSupportSearch_viewer on Viewer
            @refetchable(queryName: "GetHelpZendeskContactSupportSearchRefetchQuery") {
                zendeskArticles(isSeller: $isSeller, sellerId: $sellerId) {
                    supportArticlesSearch: articles(query: $contactSupportSearchQuery)
                        @include(if: $hasContactSupportSearchQuery) {
                        serviceId
                        title
                    }
                }
            }
        `,
        viewerRef
    );

    const fetchData = useCallback(
        (value: string) => {
            startTransition(() => {
                trackGetHelpZendeskEvent({
                    stepInteractionName: 'contact search query entered',
                    trigger: value,
                    isSeller,
                });
                setHasSearched(true);
                dispatch({
                    type: ACTIONS.SET_SHOW_FURTHER_SUPPORT,
                    value: true,
                });
                dispatch({
                    type: ACTIONS.SET_CONTACT_SUPPORT_SEARCH_QUERY,
                    query: value,
                });
                dispatch({ type: ACTIONS.SET_DISPLAY_FOOTER, value: true });
                refetch({ query: value });
            });
        },
        [refetch, startTransition, dispatch, isSeller]
    );

    const [debouncedFetchData] = useDebouncedCallback(fetchData, 200);

    const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        setQuery(value);

        if (value.length >= MIN_SEARCH_LENGTH || !value) {
            debouncedFetchData(value);
        }
    };

    const { zendeskArticles } = viewer;
    const suggestions = (zendeskArticles?.supportArticlesSearch || []).slice(0, 5);

    const searchIcon = (
        <div
            className={classnames(
                styles.searchButton,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                { [dibsCss.bgNoir]: !!query && !isSeller },
                { [dibsCss.bgDealerprimary]: !!query && isSeller }
            )}
        >
            <MagnifyingGlass
                className={classnames(dibsCss.h20px, dibsCss.w20px, {
                    [dibsCss.fillWhite]: !!query,
                })}
            />
        </div>
    );

    return (
        <>
            <h1 className={dibsCss.sassyFontHeaderMedium}>
                <FormattedMessage
                    id="getHelp.GetHelpZendeskContactSupportSearch.title"
                    defaultMessage="Contact Support"
                />
            </h1>
            <div className={classnames(dibsCss.mbSmall, dibsCss.sassyFontBodyMedium)}>
                <FormattedMessage
                    id="getHelp.GetHelpZendeskContactSupportSearch.subtitle"
                    defaultMessage="Tell us what you need help with"
                />
            </div>
            <Input
                size="medium"
                dataTn="get-help-zendesk-articles-search-input"
                placeholder={intl.formatMessage(messages.placeholder)}
                value={query}
                onChange={onChange}
                rightDecoratorClass={styles.rightDecorator}
                rightDecorator={
                    <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
                        {query && (
                            <Link
                                onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setQuery('');
                                }}
                            >
                                <span className={dibsCss.flex}>
                                    <Close
                                        className={classnames(
                                            dibsCss.w14px,
                                            dibsCss.h14px,
                                            dibsCss.mrXsmall
                                        )}
                                    />
                                </span>
                            </Link>
                        )}
                        {searchIcon}
                    </div>
                }
            />
            <div className={dibsCss.mtMedium}>
                {fetching && <Spinner />}
                {hasSearched && !suggestions.length && !!contactSupportSearchQuery && !fetching && (
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskContactSupportSearch.noResults"
                        defaultMessage="We couldn’t find any results for “{query}”. Try rephrasing your search query or contact support for further assistance."
                        values={{
                            query: contactSupportSearchQuery,
                        }}
                    />
                )}
                {!fetching && !!suggestions.length && (
                    <>
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.textSatan,
                                dibsCss.mbSmall,
                                dibsCss.sassyFontBody
                            )}
                        >
                            <div className={classnames(dibsCss.mrXsmall, dibsCss.fillShagreen600)}>
                                <QuestionMark variation="20Circle" />
                            </div>
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskContactSupportSearch.suggestedArticles"
                                defaultMessage="Suggested Articles"
                            />
                        </div>
                        <ul
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.flexCol,
                                dibsCss.gapXxsmall,
                                dibsCss.p0
                            )}
                        >
                            {suggestions?.map(article => (
                                <li key={article?.serviceId} className={styles.listItem}>
                                    <Link
                                        onClick={() => {
                                            trackGetHelpZendeskEvent({
                                                stepInteractionName: 'suggested article clicked',
                                                trigger: article?.title || '',
                                                isSeller,
                                            });
                                            dispatch({
                                                type: ACTIONS.OPEN_ARTICLE,
                                                articleId: (article?.serviceId || '').split('-')[0],
                                            });
                                            dispatch({
                                                type: ACTIONS.SET_DISPLAY_FOOTER,
                                                value: false,
                                            });
                                        }}
                                        underline="none"
                                        className={classnames(dibsCss.block, dibsCss.textLeft)}
                                    >
                                        <span
                                            className={classnames(
                                                dibsCss.sassyFontBodyMediumHeavy,
                                                dibsCss.textNoir
                                            )}
                                        >
                                            {article?.title}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </>
    );
};

export { GetHelpZendeskContactSupportSearch };
